import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { QSLM403Display } from '../components/QSLMErrorBoundary';
import '../styles/suds.css';
import {
  LayoutDashboard, GitBranch, FlaskConical, Users, ShieldCheck, Settings,
  Server, Cpu, HardDrive, Database, CreditCard, Cloud, MessageSquare,
  Shield, Phone, Printer, Globe, Lock, AlertTriangle, Bell, Key,
  LockKeyhole, Activity, ArrowRightLeft, Stethoscope, Rocket, Monitor,
  Terminal, TrendingUp, DollarSign, ShoppingCart, BarChart3, Mail,
  Webhook, Plus, CheckCircle2, XCircle, Clock, Eye, Zap, Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET_URL = process.env.REACT_APP_BACKEND_URL;

// Screen background mockup images — user's actual artwork used as visual layer
const SCREEN_BG = {
  'overview': `${ASSET_URL}/api/assets/file/5a38782f-8753-4567-86a9-5a79d2688650.png`,
  'task-manager': `${ASSET_URL}/api/assets/file/77eadec5-8eb1-4a57-a0ff-ec96f0e9d265.png`,
  'alert-center': `${ASSET_URL}/api/assets/file/664e32d6-bbda-4ecc-a916-80dc1a5630d5.png`,
  'retail': `${ASSET_URL}/api/assets/file/9a9efdb3-fae6-4d19-8a6f-b18eeec534d8.png`,
  'finance': `${ASSET_URL}/api/assets/file/306bde36-cdea-43c8-b492-cd98ebb085f6.png`,
  'marketing': `${ASSET_URL}/api/assets/file/216b3894-75a5-444c-9058-68a65d486ec7.png`,
  'capability-gates': `${ASSET_URL}/api/assets/file/e60aa9a2-fbca-4e1e-9d9b-4a1e0623f8f9.png`,
  'user-settings': `${ASSET_URL}/api/assets/file/8bd54673-3369-4318-9a6d-45fe8d507a3f.png`,
  'topology': `${ASSET_URL}/api/assets/file/5a38782f-8753-4567-86a9-5a79d2688650.png`,
  'diagnostics': `${ASSET_URL}/api/assets/file/664e32d6-bbda-4ecc-a916-80dc1a5630d5.png`,
};

/* ============================================================================
   SUDS GOD-VIEW CONSOLE — Pixel-Perfect from User's Mockup Designs
   8 Screens: Overview, Task Manager, User Settings, Alert Center,
              Retail, Finance, Capability Gates, Marketing
   Visual: DNA helixes, hex grids, circuit traces, neon glows, metallic text
   ============================================================================ */

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'lanes', label: 'Lanes', icon: GitBranch },
  { id: 'shiplab', label: 'Ship Lab', icon: FlaskConical },
  { id: 'agents', label: 'Agents', icon: Users },
  { id: 'security', label: 'Security', icon: ShieldCheck },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const HEADER_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'task-manager', label: 'Lanes' },
  { id: 'topology', label: 'Topology' },
  { id: 'alert-center', label: 'Alert Center' },
  { id: 'retail', label: 'Retail' },
  { id: 'finance', label: 'Finance' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'capability-gates', label: 'Capability Gates' },
  { id: 'diagnostics', label: 'Diagnostics' },
  { id: 'user-settings', label: 'User Settings' },
];

const QUICK_ACTIONS = [
  { id: 'transfer', label: 'TRANSFER', icon: ArrowRightLeft },
  { id: 'diagnose', label: 'DIAGNOSE', icon: Stethoscope },
  { id: 'deploy', label: 'DEPLOY', icon: Rocket },
  { id: 'monitor', label: 'MONITOR', icon: Monitor },
  { id: 'console', label: 'CONSOLE', icon: Terminal },
];

const TAB_TITLE_COLORS = {
  'overview': '#fff',
  'task-manager': '#00F0FF',
  'topology': '#00F0FF',
  'alert-center': '#fff',
  'retail': '#fff',
  'finance': '#fff',
  'marketing': '#fff',
  'capability-gates': '#D4AF37',
  'diagnostics': '#00F0FF',
  'user-settings': '#fff',
};

// === CARD WRAPPER — uses ch-card from design system, with neon accent ===
function NP({ color = '#00F0FF', children, className = '' }) {
  return (
    <div className={`ch-card relative overflow-hidden ${className}`}
      style={{ borderColor: `${color}30`, boxShadow: `0 0 15px ${color}10` }}>
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: color, opacity: 0.03, filter: 'blur(30px)' }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

// DNA helix animations handled in global background SVGs

// === OVERVIEW ===
function OverviewPanel({ health, devices }) {
  return (
    <div className="space-y-5" data-testid="overview-panel">
      <div className="grid grid-cols-4 gap-4">
        {[
          { l: 'System Status:', v: 'OPTIMAL', c: '#00F0FF', dot: true },
          { l: 'Active Lanes:', v: '3/3 CLEAN', c: '#00F0FF' },
          { l: 'Connected Devices:', v: String(devices.length || 24), c: '#00F0FF' },
          { l: 'Events/min:', v: '847', c: '#00F0FF' },
        ].map((m, i) => (
          <NP key={i} color={m.c}><div className="p-4">
            <div className="text-[10px] text-gray-400 tracking-wide mb-2">{m.l}</div>
            <div className="text-2xl font-black text-white flex items-center gap-2" style={{ fontFamily: 'Orbitron', textShadow: `0 0 20px ${m.c}40` }}>
              {m.dot && <span className="suds-dot-green" />} {m.v}
            </div>
          </div></NP>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold mb-4 tracking-widest suds-cyan-text" style={{ fontFamily: 'Orbitron' }}>FRONTEND AGENT</h3>
          {[['git branch', 'main'], ['Status', 'clean'], ['Change count', '0'], ['Last lock', '2h ago'], ['Test status', 'PASS', '#22C55E']].map(([l, v, vc], i) => (
            <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/[0.03]">
              <span className="text-[11px] text-gray-500">{l}</span>
              <span className="text-[11px] font-bold" style={{ color: vc || '#fff', textShadow: vc ? `0 0 10px ${vc}60` : 'none' }}>{v}</span>
            </div>
          ))}
        </div></NP>

        <NP color="#D4AF37"><div className="p-5">
          <h3 className="text-xs font-bold mb-4 tracking-widest" style={{ fontFamily: 'Orbitron', color: '#D4AF37', textShadow: '0 0 12px #D4AF3740' }}>CORE CANON / VAULT</h3>
          <div className="flex justify-between items-center py-1.5 border-b border-white/[0.03]"><span className="text-[11px] text-gray-500">Lock status</span><span className="text-[11px] text-white font-bold flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" style={{ color: '#D4AF37', filter: 'drop-shadow(0 0 4px #D4AF37)' }} /> LOCKED</span></div>
          <div className="flex justify-between items-center py-1.5 border-b border-white/[0.03]"><span className="text-[11px] text-gray-500">Fingerprint</span><span className="text-[11px] text-white font-mono">a7f3...c912</span></div>
          <div className="flex justify-between items-center py-1.5 border-b border-white/[0.03]"><span className="text-[11px] text-gray-500">Policy engine</span><span className="text-[11px] font-bold" style={{ color: '#22C55E', textShadow: '0 0 10px #22C55E60' }}>ACTIVE</span></div>
          <div className="flex justify-between items-center py-1.5"><span className="text-[11px] text-gray-500">Drift</span><span className="text-[11px] text-white font-bold">NONE</span></div>
        </div></NP>

        <NP color="#FF00E5"><div className="p-5">
          <h3 className="text-xs font-bold mb-4 tracking-widest suds-magenta-text" style={{ fontFamily: 'Orbitron' }}>BACKEND AGENT</h3>
          {[['Branch', 'main'], ['Status', 'clean'], ['API health', '200 OK', '#22C55E'], ['DB connection', 'NOMINAL'], ['Last deploy', '47m ago']].map(([l, v, vc], i) => (
            <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/[0.03] last:border-0">
              <span className="text-[11px] text-gray-500">{l}</span>
              <span className="text-[11px] font-bold" style={{ color: vc || '#fff', textShadow: vc ? `0 0 10px ${vc}60` : 'none' }}>{v}</span>
            </div>
          ))}
        </div></NP>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <NP color="#6366F1"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Job Braid Timeline</h3>
          <div className="h-20 flex items-end gap-[2px]">
            {Array.from({ length: 40 }).map((_, i) => {
              const c = ['#00F0FF', '#FF00E5', '#F97316', '#6366F1'][i % 4];
              const h = 15 + Math.sin(i * 0.4) * 30 + (i % 7) * 5;
              return <div key={i} className="flex-1 rounded-t transition-all" style={{ height: `${Math.min(h, 95)}%`, background: `linear-gradient(to top, ${c}, ${c}40)`, boxShadow: `0 0 4px ${c}40` }} />;
            })}
          </div>
          <div className="flex gap-4 mt-3">
            {[['#00F0FF', 'events'], ['#FF00E5', 'agents'], ['#F97316', 'sherbet orange'], ['#6366F1', 'congo blue/indigo']].map(([c, l], i) => (
              <div key={i} className="flex items-center gap-1.5 text-[8px] text-gray-400"><span className="w-2 h-2 rounded-full" style={{ background: c, boxShadow: `0 0 4px ${c}` }} />{l}</div>
            ))}
          </div>
        </div></NP>

        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Host Telemetry</h3>
          <div className="grid grid-cols-4 gap-3">
            {[['CPU', 42, '#00F0FF'], ['RAM', 67, '#8B5CF6'], ['Disk', 34, '#22C55E'], ['Net', 78, '#F97316']].map(([l, v, c], i) => (
              <div key={i} className="text-center">
                <div className="relative w-14 h-14 mx-auto">
                  <svg className="w-14 h-14 transform -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#1a1a2e" strokeWidth="3" />
                    <circle cx="18" cy="18" r="14" fill="none" stroke={c} strokeWidth="3" strokeDasharray={`${v * 0.88} 100`} strokeLinecap="round" style={{ filter: `drop-shadow(0 0 4px ${c})` }} />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-white" style={{ fontFamily: 'Orbitron' }}>{v}%</span>
                </div>
                <div className="text-[9px] text-gray-400 mt-1.5 font-bold">{l}</div>
              </div>
            ))}
          </div>
        </div></NP>

        <NP color="#22C55E"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Terminal Code</h3>
          <div className="font-mono text-[10px] space-y-1.5">
            <div><span className="text-emerald-400" style={{ textShadow: '0 0 6px #22C55E60' }}>$</span> <span className="text-gray-300">suds status --all</span></div>
            <div><span className="text-cyan-400">INFO</span> Frontend Agent: <span className="font-bold" style={{ color: '#22C55E', textShadow: '0 0 8px #22C55E60' }}>CLEAN</span></div>
            <div><span className="text-cyan-400">INFO</span> Backend Agent: <span className="font-bold" style={{ color: '#22C55E', textShadow: '0 0 8px #22C55E60' }}>NOMINAL</span></div>
            <div><span className="text-cyan-400">INFO</span> Vault Lock: <span className="font-bold" style={{ color: '#D4AF37', textShadow: '0 0 8px #D4AF3760' }}>ENGAGED</span></div>
            <div><span className="text-cyan-400">INFO</span> Policy Engine: <span className="font-bold" style={{ color: '#22C55E', textShadow: '0 0 8px #22C55E60' }}>ACTIVE</span></div>
            <div><span className="text-emerald-400" style={{ textShadow: '0 0 6px #22C55E60' }}>$</span> <span className="suds-cursor text-white">_</span></div>
          </div>
        </div></NP>
      </div>
    </div>
  );
}

// === ALERT CENTER ===
function AlertCenterPanel() {
  const alerts = [
    { severity: 'CRITICAL', time: '10:15 AM', desc: 'Database Cluster Failure - Primary Node Unresponsive', actions: ['Failover', 'Diagnose'], cls: 'suds-alert-critical' },
    { severity: 'HIGH', time: '10:02 AM', desc: 'API Latency Spike > 500ms in EU Region', actions: ['Scale Out', 'Investigate'], cls: 'suds-alert-high' },
    { severity: 'WARNING', time: '09:45 AM', desc: 'Memory Usage > 85% on Worker Pool 3', actions: ['Monitor', 'Acknowledge'], cls: 'suds-alert-warning' },
    { severity: 'INFO', time: '09:30 AM', desc: 'Scheduled Maintenance Completed Successfully', actions: ['Dismiss'], cls: 'suds-alert-info' },
  ];
  const rules = [
    'CPU > 90% → Scale', 'Deploy Fails → Rollback + Slack', 'Security Scan BLOCK → Halt Pipeline',
    'High Error Rate → Create Incident', 'New User Signup → Welcome Email + Slack',
  ];

  return (
    <div className="space-y-5" data-testid="alert-center-panel">
      <div className="grid grid-cols-4 gap-4">
        {[['Active Rules:', '47', '#00F0FF'], ['Triggers Today:', '312', '#00F0FF'], ['Incidents:', '0', '#00F0FF'], ['Uptime:', '99.99%', '#22C55E']].map(([l, v, c], i) => (
          <NP key={i} color={c}><div className="p-4">
            <div className="text-[10px] text-gray-400 mb-1">{l}</div>
            <div className="text-2xl font-black" style={{ fontFamily: 'Orbitron', color: c === '#22C55E' ? c : '#fff', textShadow: `0 0 15px ${c}40` }}>{v}</div>
          </div></NP>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          {alerts.map((a, i) => (
            <div key={i} className={`rounded-lg p-4 ${a.cls}`} style={{ background: 'linear-gradient(145deg, #0a0a16, #060610)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black tracking-wider" style={{ fontFamily: 'Orbitron' }}>{a.severity}</span>
                <span className="text-[9px] text-gray-400">{a.time}</span>
              </div>
              <p className="text-[11px] text-gray-300 mb-2">{a.desc}</p>
              <div className="flex gap-2">
                {a.actions.map((act, j) => (
                  <button key={j} className="text-[9px] px-2.5 py-1 rounded border border-white/10 text-gray-300 hover:bg-white/[0.05] transition-all">[{act}]</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {rules.map((r, i) => (
            <NP key={i} color="#00F0FF"><div className="p-3 flex items-center justify-between">
              <span className="text-[11px] text-gray-200">{r}</span>
              <div className="flex items-center gap-2">
                <span className="text-[8px] text-emerald-400">(Active)</span>
                <div className="suds-toggle active"><div className="suds-toggle-knob" /></div>
              </div>
            </div></NP>
          ))}
          <NP color="#D4AF37"><div className="p-4">
            <h4 className="text-[10px] font-bold text-gray-400 mb-3 tracking-wider" style={{ fontFamily: 'Orbitron' }}>CONNECTED SERVICES</h4>
            <div className="grid grid-cols-4 gap-2">
              {['PagerDuty', 'Slack', 'OpsGenie', 'Email', 'SMS', 'Webhook'].map((s, i) => (
                <div key={i} className="text-center p-2 rounded-lg border border-white/[0.05] hover:bg-white/[0.02] cursor-pointer transition-all">
                  <span className="suds-dot-green mx-auto mb-1 block" style={{ width: 6, height: 6 }} />
                  <span className="text-[8px] text-gray-400">{s}</span>
                </div>
              ))}
              <div className="text-center p-2 rounded-lg border border-dashed border-white/10 hover:bg-white/[0.02] cursor-pointer">
                <Plus className="w-3 h-3 text-gray-500 mx-auto mb-1" />
                <span className="text-[8px] text-gray-500">Add</span>
              </div>
            </div>
          </div></NP>
        </div>
      </div>
    </div>
  );
}

// === RETAIL ===
function RetailPanel() {
  return (
    <div className="space-y-5" data-testid="retail-panel">
      <div className="grid grid-cols-4 gap-4">
        {[['REVENUE TODAY', '$12,847', '#00F0FF'], ['ORDERS', '156', '#8B5CF6'], ['AVERAGE ORDER', '$82', '#D4AF37'], ['INVENTORY ITEMS', '2,340', '#00F0FF']].map(([l, v, c], i) => (
          <NP key={i} color={c}><div className="p-4">
            <div className="text-[10px] text-gray-400 tracking-wide mb-1">{l}</div>
            <div className="text-2xl font-black" style={{ fontFamily: 'Orbitron', color: c, textShadow: `0 0 15px ${c}40` }}>{v}</div>
          </div></NP>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NP color="#D4AF37"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>ORDERS TABLE</h3>
          <table className="w-full text-[10px]">
            <thead><tr className="text-gray-500 border-b border-white/[0.05]">
              <th className="text-left py-2">Order #</th><th className="text-left py-2">Customer</th><th className="text-left py-2">Items</th><th className="text-left py-2">Amount</th><th className="text-left py-2">Status</th>
            </tr></thead>
            <tbody>
              {[['#ORD-1021', 'John D.', '3 items', '$250', 'Fulfilled', 'suds-badge-fulfilled'],
                ['#ORD-1022', 'Sarah K.', '1 item', '$55', 'Processing', 'suds-badge-processing'],
                ['#ORD-1023', 'Mike R.', '2 items', '$120', 'Pending', 'suds-badge-pending'],
                ['#ORD-1024', 'Sarah M.', '1 item', '$150', 'Fulfilled', 'suds-badge-fulfilled'],
                ['#ORD-1025', 'John D.', '3 items', '$150', 'Pending', 'suds-badge-pending'],
              ].map(([id, cust, items, amt, status, cls], i) => (
                <tr key={i} className="border-b border-white/[0.03] text-gray-300">
                  <td className="py-2.5 font-mono text-cyan-400">{id}</td><td className="py-2.5">{cust}</td><td className="py-2.5">{items}</td><td className="py-2.5 font-bold">{amt}</td>
                  <td className="py-2.5"><span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${cls}`}>{status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></NP>

        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>REAL-TIME SALES</h3>
          <div className="relative h-32">
            <svg className="w-full h-full" viewBox="0 0 300 100" preserveAspectRatio="none">
              <defs>
                <linearGradient id="sales-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 25, 50, 75, 100].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#1a1a2e" strokeWidth="0.5" />)}
              <path d="M0,80 L25,60 L50,70 L75,40 L100,55 L125,30 L150,45 L175,20 L200,35 L225,15 L250,25 L275,10 L300,20" fill="url(#sales-grad)" />
              <path d="M0,80 L25,60 L50,70 L75,40 L100,55 L125,30 L150,45 L175,20 L200,35 L225,15 L250,25 L275,10 L300,20" fill="none" stroke="#00F0FF" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 4px #00F0FF)' }} />
            </svg>
          </div>
        </div></NP>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <NP color="#D4AF37"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>TOP PRODUCTS</h3>
          {[['Premium Beat Pack', 250], ['Neon City Visuals', 103], ['Creator Toolkit', 89], ['Brand Identity Kit', 77]].map(([n, c], i) => (
            <div key={i} className="flex justify-between py-2 border-b border-white/[0.03] text-[10px]"><span className="text-gray-300">{n}</span><span className="text-white font-bold">{c}</span></div>
          ))}
        </div></NP>
        <NP color="#8B5CF6"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>INVENTORY ALERTS</h3>
          {[['Low Stock: Beat Pack Premium', '#F59E0B'], ['Reorder: Neon City Bundle', '#F97316'], ['Low Stock: Merch Collection', '#F59E0B']].map(([desc, c], i) => (
            <div key={i} className="flex items-center gap-2 py-2.5 border-b border-white/[0.03]">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c, filter: `drop-shadow(0 0 3px ${c})` }} />
              <span className="text-[10px] text-gray-300">{desc}</span>
            </div>
          ))}
        </div></NP>
        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>STORE LOCATIONS</h3>
          {[['VigXI Store', 'US-East', 'Online'], ['Shopify Edge', 'Cloud-CDN', 'Online'], ['Pop-Up LA', 'US-West', 'Scheduled']].map(([n, loc, st], i) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.03]">
              <div><div className="text-[10px] text-gray-200 font-medium">{n}</div><div className="text-[8px] text-gray-500">{loc}</div></div>
              <span className={`text-[9px] font-bold ${st === 'Online' ? 'text-emerald-400' : 'text-yellow-400'}`} style={{ textShadow: `0 0 6px ${st === 'Online' ? '#22C55E' : '#F59E0B'}40` }}>{st}</span>
            </div>
          ))}
        </div></NP>
      </div>
    </div>
  );
}

// === FINANCE ===
function FinancePanel() {
  return (
    <div className="space-y-5" data-testid="finance-panel">
      <div className="grid grid-cols-5 gap-3">
        {[['Revenue', '$247,500', '#00F0FF'], ['MRR', '$18,750', '#8B5CF6'], ['Active Subs', '487', '#D4AF37'], ['Pending Invoices', '23', '#FF00E5'], ['Refund Rate', '1.2%', '#00F0FF']].map(([l, v, c], i) => (
          <NP key={i} color={c}><div className="p-3">
            <div className="text-[9px] text-gray-400 mb-1">{l}</div>
            <div className="text-lg font-black" style={{ fontFamily: 'Orbitron', color: c, textShadow: `0 0 12px ${c}40` }}>{v}</div>
          </div></NP>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NP color="#D4AF37"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>INVOICES</h3>
          <table className="w-full text-[10px]">
            <thead><tr className="text-gray-500 border-b border-white/[0.05]"><th className="text-left py-2">Invoice#</th><th className="text-left py-2">Client</th><th className="text-left py-2">Amount</th><th className="text-left py-2">Status</th></tr></thead>
            <tbody>
              {[['INV-1001', 'Acme Corp', '$5,000', 'PAID', 'suds-badge-paid'], ['INV-1002', 'Globex', '$2,500', 'PENDING', 'suds-badge-pending'], ['INV-1003', 'Initech', '$1,200', 'OVERDUE', 'suds-badge-overdue']].map(([id, cl, amt, st, cls], i) => (
                <tr key={i} className="border-b border-white/[0.03] text-gray-300">
                  <td className="py-2.5 font-mono text-cyan-400">{id}</td><td className="py-2.5">{cl}</td><td className="py-2.5 font-bold">{amt}</td>
                  <td className="py-2.5"><span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${cls}`}>{st}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></NP>
        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-4 tracking-widest" style={{ fontFamily: 'Orbitron' }}>REVENUE TREND</h3>
          <div className="h-32">
            <svg className="w-full h-full" viewBox="0 0 300 100" preserveAspectRatio="none">
              <defs><linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22C55E" stopOpacity="0.3" /><stop offset="100%" stopColor="#22C55E" stopOpacity="0" /></linearGradient></defs>
              {[0, 25, 50, 75, 100].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#1a1a2e" strokeWidth="0.5" />)}
              <path d="M0,90 L30,80 L60,75 L90,60 L120,65 L150,50 L180,40 L210,45 L240,30 L270,20 L300,15" fill="url(#rev-grad)" />
              <path d="M0,90 L30,80 L60,75 L90,60 L120,65 L150,50 L180,40 L210,45 L240,30 L270,20 L300,15" fill="none" stroke="#22C55E" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 4px #22C55E)' }} />
            </svg>
          </div>
        </div></NP>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <NP color="#8B5CF6"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>PLAN DISTRIBUTION</h3>
          <div className="flex items-center justify-center">
            <svg width="120" height="120" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#D4AF37" strokeWidth="3" strokeDasharray="42 58" strokeDashoffset="0" className="suds-donut-ring" style={{ color: '#D4AF37' }} />
              <circle cx="18" cy="18" r="15" fill="none" stroke="#14B8A6" strokeWidth="3" strokeDasharray="24 76" strokeDashoffset="-42" className="suds-donut-ring" style={{ color: '#14B8A6' }} />
              <circle cx="18" cy="18" r="15" fill="none" stroke="#EC4899" strokeWidth="3" strokeDasharray="23 77" strokeDashoffset="-66" className="suds-donut-ring" style={{ color: '#EC4899' }} />
              <circle cx="18" cy="18" r="15" fill="none" stroke="#818CF8" strokeWidth="3" strokeDasharray="11 89" strokeDashoffset="-89" className="suds-donut-ring" style={{ color: '#818CF8' }} />
              <text x="18" y="16" textAnchor="middle" fill="#D4AF37" fontSize="4" fontWeight="bold" style={{ fontFamily: 'Orbitron' }}>45%</text>
              <text x="18" y="21" textAnchor="middle" fill="#aaa" fontSize="2.5">Brand</text>
            </svg>
          </div>
          <div className="flex justify-center gap-4 mt-3">
            {[['#D4AF37', 'A'], ['#14B8A6', 'B'], ['#EC4899', 'C'], ['#818CF8', 'D']].map(([c, l], i) => (
              <div key={i} className="flex items-center gap-1 text-[8px] text-gray-400"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</div>
            ))}
          </div>
        </div></NP>
        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>RECENT ACTIVITY</h3>
          {[['New subscription: TechGiant', '#22C55E', Clock], ['Invoice PAID: Acme Corp', '#00F0FF', CheckCircle2], ['Refund Processed: User123', '#F97316', ArrowRightLeft], ['Pending Invoice Alert: Globex', '#F59E0B', AlertTriangle]].map(([desc, c, Icon], i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 border-b border-white/[0.03]">
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c, filter: `drop-shadow(0 0 3px ${c})` }} />
              <span className="text-[10px] text-gray-300">{desc}</span>
            </div>
          ))}
        </div></NP>
        <div className="space-y-3">
          <NP color="#D4AF37"><div className="p-4">
            <h4 className="text-[10px] font-bold tracking-wider mb-3" style={{ fontFamily: 'Orbitron', color: '#D4AF37' }}>QUICK ACTIONS</h4>
            {['Generate Invoice', 'Process Refund', 'Export Report'].map((a, i) => (
              <button key={i} className="w-full mb-2 py-2 rounded-lg suds-gold-btn text-[10px]">{a}</button>
            ))}
          </div></NP>
        </div>
      </div>
    </div>
  );
}

// === CAPABILITY GATES ===
function CapabilityGatesPanel() {
  const gates = [
    { title: 'Conversation Depth', levels: [{ l: 'Basic', on: true }, { l: 'Enhanced', on: true }, { l: 'Deep', on: false, lock: 'Tier 3 Required' }], color: '#00F0FF' },
    { title: 'Memory Retention', levels: [{ l: 'Short-Term', on: true }, { l: 'Mid-Term', on: false }, { l: 'Long-Term', on: false, lock: 'Tier 3 Required' }], color: '#FF00E5' },
    { title: 'Execution Authority', levels: [{ l: 'Observe', on: true }, { l: 'Assist', on: true }, { l: 'Autonomous', on: false, lock: 'Tier 3 Required' }], color: '#D4AF37' },
    { title: 'Spatial Presence', levels: [{ l: 'Virtual', on: true }, { l: 'Local', on: false }, { l: 'Global', on: false, lock: 'Tier 3 Required' }], color: '#6366F1' },
  ];
  return (
    <div data-testid="capability-gates-panel">
      <div className="text-center mb-6">
        <h2 className="text-lg font-black tracking-widest suds-gold-text" style={{ fontFamily: 'Orbitron' }}>QUADRA CORE</h2>
        <div className="text-[10px] text-gray-500 mt-1">CAPABILITY GATES</div>
      </div>
      <div className="grid grid-cols-2 gap-5">
        {gates.map((g, i) => (
          <NP key={i} color={g.color}><div className="p-5">
            <h3 className="text-xs font-bold tracking-widest mb-4" style={{ fontFamily: 'Orbitron', color: g.color, textShadow: `0 0 10px ${g.color}40` }}>{g.title}</h3>
            <div className="space-y-3">
              {g.levels.map((lv, j) => (
                <div key={j} className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-300">{lv.l}</span>
                  <div className="flex items-center gap-2">
                    {lv.lock && <span className="text-[8px] text-gray-600 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> {lv.lock}</span>}
                    <div className={`suds-toggle ${lv.on ? 'active' : 'inactive'}`}><div className="suds-toggle-knob" /></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 h-1.5 rounded-full overflow-hidden" style={{ background: '#1a1a2e' }}>
              <div className="h-full rounded-full" style={{ width: `${g.levels.filter(l => l.on).length / g.levels.length * 100}%`, background: `linear-gradient(90deg, ${g.color}, ${g.color}80)`, boxShadow: `0 0 8px ${g.color}40` }} />
            </div>
          </div></NP>
        ))}
      </div>
    </div>
  );
}

// === MARKETING ===
function MarketingPanel() {
  const [subTab, setSubTab] = useState('overview');
  const subTabs = ['Overview', 'Campaigns', 'Audience', 'Content', 'Analytics'];
  const calendarDates = [
    [30, 31, 1, 2, 3],
    [6, 7, 8, 9, 10],
    [13, 14, 15, 16, 17],
  ];
  const calendarIcons = { 31: { icon: '🚩', bg: '#3B82F6' }, 1: { icon: '📅', bg: '#F59E0B' }, 2: { icon: '🚀', bg: '#EC4899' }, 9: { icon: '▶', bg: '#14B8A6' }, 16: { icon: '✉', bg: '#EC4899' } };

  return (
    <div className="space-y-5" data-testid="marketing-panel">
      {/* Sub-tabs */}
      <div className="flex gap-4 border-b border-white/[0.05] pb-2">
        {subTabs.map(t => (
          <button key={t} onClick={() => setSubTab(t.toLowerCase())}
            className={`text-[11px] font-medium pb-1 transition-all ${subTab === t.toLowerCase() ? 'text-white border-b-2 border-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}>{t}</button>
        ))}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-5 gap-3">
        {[['Active Campaigns', '8', '#00F0FF'], ['Total Reach', '247K', '#FF00E5'], ['Click Rate', '4.2%', '#22C55E'], ['Conversions', '1,847', '#D4AF37'], ['Ad Spend', '$12,400', '#F97316']].map(([l, v, c], i) => (
          <NP key={i} color={c}><div className="p-4">
            <div className="text-[9px] text-gray-400 mb-1">{l}</div>
            <div className="text-xl font-black" style={{ fontFamily: 'Orbitron', color: c, textShadow: `0 0 12px ${c}40` }}>{v}</div>
          </div></NP>
        ))}
      </div>

      {/* Campaign Cards Row — 2 large + 2 smaller OR 3+1 layout matching mockup */}
      <div className="grid grid-cols-12 gap-4">
        {/* Summer Promo — cyan border, mini cyan line chart */}
        <div className="col-span-4"><NP color="#00F0FF"><div className="p-5">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Orbitron' }}>Summer Promo</h3>
            <span className="text-[9px] font-bold text-emerald-400">Active</span>
          </div>
          <div className="text-[9px] text-gray-400 mb-1">Budget: <span className="text-white">75%</span></div>
          <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: '#1a1a2e' }}>
            <div className="h-full rounded-full" style={{ width: '75%', background: 'linear-gradient(90deg, #00F0FF, #0088AA)', boxShadow: '0 0 6px #00F0FF40' }} />
          </div>
          <div className="h-16">
            <svg className="w-full h-full" viewBox="0 0 200 60" preserveAspectRatio="none">
              <defs><linearGradient id="sp-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity="0.15" /><stop offset="100%" stopColor="#00F0FF" stopOpacity="0" /></linearGradient></defs>
              <path d="M0,50 L30,45 L60,35 L90,40 L120,25 L150,20 L180,15 L200,10" fill="url(#sp-grad)" />
              <path d="M0,50 L30,45 L60,35 L90,40 L120,25 L150,20 L180,15 L200,10" fill="none" stroke="#00F0FF" strokeWidth="1.5" style={{ filter: 'drop-shadow(0 0 3px #00F0FF)' }} />
            </svg>
          </div>
        </div></NP></div>

        {/* Fall Launch — gold border, mini gold line chart */}
        <div className="col-span-4"><NP color="#D4AF37"><div className="p-5">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Orbitron' }}>Fall Launch</h3>
            <span className="text-[9px] font-bold" style={{ color: '#F59E0B' }}>Scheduled</span>
          </div>
          <div className="text-[9px] text-gray-400 mb-1">Budget: <span className="text-white">20%</span></div>
          <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: '#1a1a2e' }}>
            <div className="h-full rounded-full" style={{ width: '20%', background: 'linear-gradient(90deg, #D4AF37, #B8962E)', boxShadow: '0 0 6px #D4AF3740' }} />
          </div>
          <div className="h-16">
            <svg className="w-full h-full" viewBox="0 0 200 60" preserveAspectRatio="none">
              <defs><linearGradient id="fl-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity="0.15" /><stop offset="100%" stopColor="#D4AF37" stopOpacity="0" /></linearGradient></defs>
              <path d="M0,45 L40,40 L80,42 L120,38 L160,35 L200,30" fill="url(#fl-grad)" />
              <path d="M0,45 L40,40 L80,42 L120,38 L160,35 L200,30" fill="none" stroke="#D4AF37" strokeWidth="1.5" style={{ filter: 'drop-shadow(0 0 3px #D4AF37)' }} />
            </svg>
          </div>
        </div></NP></div>

        {/* Campaigns (general) — no colored border, grey line */}
        <div className="col-span-4"><NP color="#555"><div className="p-5">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Orbitron' }}>Campaigns</h3>
          </div>
          <div className="text-[9px] text-gray-400 mb-1">Budget: <span className="text-white">100%</span></div>
          <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: '#1a1a2e' }}>
            <div className="h-full rounded-full" style={{ width: '100%', background: '#555' }} />
          </div>
          <div className="h-16">
            <svg className="w-full h-full" viewBox="0 0 200 60" preserveAspectRatio="none">
              <path d="M0,40 L30,35 L60,38 L90,30 L120,32 L150,25 L180,28 L200,22" fill="none" stroke="#555" strokeWidth="1.5" />
            </svg>
          </div>
        </div></NP></div>
      </div>

      {/* Spring Sale — magenta, full width below */}
      <NP color="#FF00E5"><div className="p-5">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-bold" style={{ fontFamily: 'Orbitron', color: '#FF00E5' }}>Spring Sale</h3>
          <span className="text-[9px] font-bold text-gray-400">Completed</span>
        </div>
        <div className="text-[9px] text-gray-400 mb-1">Budget: <span className="text-white">100%</span></div>
        <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: '#1a1a2e' }}>
          <div className="h-full rounded-full" style={{ width: '100%', background: 'linear-gradient(90deg, #FF00E5, #AA00AA)', boxShadow: '0 0 6px #FF00E540' }} />
        </div>
        <div className="h-20">
          <svg className="w-full h-full" viewBox="0 0 400 70" preserveAspectRatio="none">
            <defs><linearGradient id="ss-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF00E5" stopOpacity="0.15" /><stop offset="100%" stopColor="#FF00E5" stopOpacity="0" /></linearGradient></defs>
            <path d="M0,60 L50,55 L100,50 L150,40 L200,35 L250,25 L300,20 L350,15 L400,10" fill="url(#ss-grad)" />
            <path d="M0,60 L50,55 L100,50 L150,40 L200,35 L250,25 L300,20 L350,15 L400,10" fill="none" stroke="#FF00E5" strokeWidth="1.5" style={{ filter: 'drop-shadow(0 0 3px #FF00E5)' }} />
          </svg>
        </div>
      </div></NP>

      {/* Bottom Row: Audience Reach | Content Calendar + Channel Breakdown + A/B Test */}
      <div className="grid grid-cols-12 gap-4">
        {/* Audience Reach — spans 6 cols */}
        <div className="col-span-6"><NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Audience Reach</h3>
          <div className="relative h-28">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[8px] text-gray-500 pr-2">
              <span>100K</span><span>75K</span><span>50K</span><span>25K</span><span>0</span>
            </div>
            <div className="ml-8 h-full">
              <svg className="w-full h-full" viewBox="0 0 300 100" preserveAspectRatio="none">
                <defs><linearGradient id="ar-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#14B8A6" stopOpacity="0.25" /><stop offset="100%" stopColor="#0D0D0D" stopOpacity="0" /></linearGradient></defs>
                {[0, 25, 50, 75, 100].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#1a1a2e" strokeWidth="0.5" />)}
                <path d="M0,90 L50,85 L100,70 L150,55 L200,40 L250,45 L300,10" fill="url(#ar-grad)" />
                <path d="M0,90 L50,85 L100,70 L150,55 L200,40 L250,45 L300,10" fill="none" stroke="#14B8A6" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 4px #14B8A6)' }} />
              </svg>
            </div>
          </div>
          <div className="flex justify-between text-[8px] text-gray-500 mt-1 ml-8">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'].map(m => <span key={m}>{m}</span>)}
          </div>
        </div></NP></div>

        {/* Right column: Content Calendar + Channel Breakdown + A/B Test */}
        <div className="col-span-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Content Calendar */}
            <NP color="#00F0FF"><div className="p-4">
              <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Content Calendar</h3>
              <table className="w-full text-center text-[9px]">
                <thead><tr className="text-gray-500">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => <th key={d} className="py-1 font-normal">{d}</th>)}
                </tr></thead>
                <tbody>
                  {calendarDates.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((day, di) => {
                        const ci = calendarIcons[day];
                        return (
                          <td key={di} className="py-1.5">
                            <div className={`w-8 h-8 mx-auto rounded flex items-center justify-center text-[10px] ${ci ? 'text-white' : 'text-gray-600'}`}
                              style={ci ? { background: ci.bg + '30', border: `1px solid ${ci.bg}50` } : {}}>
                              {ci ? <span className="text-[10px]">{ci.icon}</span> : day}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></NP>

            {/* Channel Breakdown */}
            <NP color="#FF00E5"><div className="p-4">
              <h3 className="text-xs font-bold text-white mb-3 tracking-widest" style={{ fontFamily: 'Orbitron' }}>Channel Breakdown</h3>
              <div className="flex items-center justify-center">
                <svg width="90" height="90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#00F0FF" strokeWidth="4" strokeDasharray="35 65" className="suds-donut-ring" style={{ color: '#00F0FF' }} />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#EC4899" strokeWidth="4" strokeDasharray="25 75" strokeDashoffset="-35" className="suds-donut-ring" style={{ color: '#EC4899' }} />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#D4AF37" strokeWidth="4" strokeDasharray="20 80" strokeDashoffset="-60" className="suds-donut-ring" style={{ color: '#D4AF37' }} />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#8B5CF6" strokeWidth="4" strokeDasharray="20 80" strokeDashoffset="-80" className="suds-donut-ring" style={{ color: '#8B5CF6' }} />
                </svg>
              </div>
              <div className="flex justify-center gap-3 mt-2">
                {[['#00F0FF', 'Social'], ['#EC4899', 'Email'], ['#D4AF37', 'PPC'], ['#8B5CF6', 'Organic']].map(([c, l], i) => (
                  <div key={i} className="flex items-center gap-1 text-[8px] text-gray-400"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</div>
                ))}
              </div>
            </div></NP>
          </div>

          {/* A/B Test Results */}
          <NP color="#00F0FF"><div className="p-4">
            <h3 className="text-xs font-bold text-white mb-2 tracking-widest" style={{ fontFamily: 'Orbitron' }}>A/B Test Results</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-gray-400">Test status:</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 4px #22C55E' }} />
              <span className="text-[10px] text-emerald-400 font-bold">Success</span>
              <span className="text-[10px] text-gray-500">| test</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400">Winner:</span>
              <span className="text-[10px] font-bold" style={{ color: '#D4AF37' }}>Congo gold</span>
            </div>
          </div></NP>
        </div>
      </div>
    </div>
  );
}

// === TASK MANAGER (imported from before, keeping compact) ===
function TaskManagerPanel({ tasks }) {
  const columns = [
    { id: 'backlog', label: 'BACKLOG', color: '#00F0FF' },
    { id: 'queued', label: 'QUEUED', color: '#FF00E5' },
    { id: 'in_progress', label: 'IN PROGRESS', color: '#8B5CF6' },
    { id: 'review', label: 'REVIEW', color: '#EC4899' },
    { id: 'testing', label: 'TESTING', color: '#F97316' },
    { id: 'done', label: 'COMPLETE', color: '#22C55E' },
  ];
  const pC = { high: { bg: '#FF00E530', text: '#FF00E5', l: 'P1' }, medium: { bg: '#D4AF3730', text: '#D4AF37', l: 'P2' }, low: { bg: '#00F0FF30', text: '#00F0FF', l: 'P3' } };
  const get = (colId) => {
    const m = tasks.filter(t => t.status === colId);
    return m.length > 0 ? m : [{ id: `${colId}-1`, title: `Implement ${colId.replace('_', ' ')}`, priority: 'high', assigned_to: 'Architect', due: 'Oct 27' }, { id: `${colId}-2`, title: `Configure ${colId.replace('_', ' ')} flow`, priority: 'medium', assigned_to: 'Engineer', due: 'Oct 25' }];
  };
  return (
    <div data-testid="task-manager-panel">
      <div className="grid grid-cols-4 gap-4 mb-5">
        {[['Active Tasks:', '47', '#00F0FF'], ['Completed:', '312', '#00F0FF'], ['Sprint Progress:', '78%', '#D4AF37'], ['Events/min:', '847', '#00F0FF']].map(([l, v, c], i) => (
          <NP key={i} color={c}><div className="p-4"><div className="text-[10px] text-gray-400 mb-1">{l}</div><div className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron', textShadow: `0 0 15px ${c}30` }}>{v}</div></div></NP>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-3">
        {columns.map(col => (
          <div key={col.id}>
            <div className="text-[9px] font-bold tracking-widest mb-3 pb-2 border-b-[3px]" style={{ fontFamily: 'Orbitron', color: col.color, borderColor: col.color, textShadow: `0 0 8px ${col.color}50` }}>{col.label}</div>
            <div className="space-y-2">
              {get(col.id).map(task => {
                const p = pC[task.priority] || pC.medium;
                return (
                  <div key={task.id} className="rounded-lg p-3 border-l-[3px] hover:translate-x-0.5 transition-all" style={{ background: 'linear-gradient(145deg, #0a0a16, #060610)', borderLeftColor: col.color, boxShadow: `0 0 10px ${col.color}08` }}>
                    <div className="text-[10px] text-gray-200 font-medium leading-tight mb-2">{task.title}</div>
                    <div className="flex items-center justify-between"><span className="text-[8px] px-2 py-0.5 rounded font-bold" style={{ background: p.bg, color: p.text, boxShadow: `0 0 6px ${p.text}20` }}>{p.l}</span><span className="text-[8px] text-gray-600">{task.due || ''}</span></div>
                    <div className="flex items-center justify-between mt-2"><span className="text-[8px] text-gray-500">{task.assigned_to}</span><div className="w-5 h-5 rounded-full" style={{ background: `linear-gradient(135deg, ${col.color}30, ${col.color}10)`, border: `1px solid ${col.color}30` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// === USER SETTINGS (compact) ===
function UserSettingsPanel({ devices, integrations }) {
  return (
    <div className="space-y-5" data-testid="user-settings-panel">
      <div className="grid grid-cols-3 gap-4">
        <NP color="#D4AF37"><div className="p-5">
          <h3 className="text-xs font-bold tracking-widest mb-4" style={{ fontFamily: 'Orbitron', color: '#D4AF37', textShadow: '0 0 10px #D4AF3740' }}>SETTINGS</h3>
          {[['General', Settings, '#D4AF37'], ['Security', ShieldCheck, '#00F0FF'], ['Notifications', Bell, '#FF00E5'], ['API Keys', Key, '#00F0FF'], ['Permissions', LockKeyhole, '#FF00E5']].map(([l, Icon, c], i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border mb-2 cursor-pointer hover:bg-white/[0.03] transition-all" style={{ borderColor: `${c}20` }}>
              <Icon className="w-4 h-4" style={{ color: c, filter: `drop-shadow(0 0 3px ${c})` }} /><span className="text-[11px] text-gray-300">{l}</span>
            </div>
          ))}
          <button className="w-full mt-3 py-2.5 rounded-lg suds-gold-btn text-[10px]">Save Changes</button>
        </div></NP>
        <div className="col-span-2"><NP color="#00F0FF"><div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold tracking-widest suds-cyan-text" style={{ fontFamily: 'Orbitron' }}>DEVICE MANAGEMENT</h3>
            <div className="flex gap-2">
              <input placeholder="Search" className="px-3 py-1.5 rounded-lg text-[9px] border text-white placeholder-gray-600" style={{ background: '#0a0a14', borderColor: '#00F0FF20' }} />
              <button className="px-3 py-1.5 rounded-lg text-[9px] border text-gray-400" style={{ background: '#0a0a14', borderColor: '#ffffff10' }}>Filters</button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(devices.length > 0 ? devices.slice(0, 6) : [{name:'Server-01',node_id:'s1',location:'192.168.1.10',status:'online'},{name:'Server-02',node_id:'s2',location:'192.168.1.11',status:'online'},{name:'Gateway-A',node_id:'g1',location:'192.168.1.1',status:'online'},{name:'Gateway-B',node_id:'g2',location:'192.168.1.2',status:'degraded'},{name:'Sensor-01',node_id:'sn1',location:'192.168.1.100',status:'online'},{name:'Printer-A',node_id:'p1',location:'192.168.1.200',status:'online'}]).map(d => {
              const sc = d.status === 'online' ? '#22C55E' : '#F59E0B';
              return (
                <div key={d.node_id||d.name} className="rounded-lg p-3 border" style={{ background: 'linear-gradient(145deg, #0a0a14, #060610)', borderColor: `${sc}20`, boxShadow: `0 0 8px ${sc}08` }}>
                  <div className="flex items-center gap-2 mb-1"><Server className="w-3.5 h-3.5" style={{ color: sc, filter: `drop-shadow(0 0 3px ${sc})` }} /><span className="text-[10px] text-white font-bold">{d.name}</span></div>
                  <div className="text-[8px] text-gray-500 font-mono mb-1">{d.location}</div>
                  <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: sc, boxShadow: `0 0 6px ${sc}` }} /><span className="text-[8px] text-gray-500">Active</span></div>
                </div>
              );
            })}
          </div>
        </div></NP></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <NP color="#FF00E5"><div className="p-5">
          <h3 className="text-xs font-bold tracking-widest mb-4 suds-magenta-text" style={{ fontFamily: 'Orbitron' }}>INTEGRATIONS</h3>
          {(integrations.length > 0 ? integrations : [{name:'Slack',status:'active'},{name:'PagerDuty',status:'inactive'},{name:'GitHub',status:'active'},{name:'AWS',status:'active'},{name:'GCP',status:'active'},{name:'Stripe',status:'active'}]).map((intg, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border mb-2" style={{ background: '#08080f', borderColor: '#1a1a2e' }}>
              <div className="flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full ${intg.status === 'active' ? 'suds-dot-green' : 'suds-dot-red'}`} style={{ width: 6, height: 6 }} />
                <span className="text-[11px] text-gray-200">{intg.name}</span>
                <span className="text-[8px] text-gray-600">{intg.status === 'active' ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`suds-toggle ${intg.status === 'active' ? 'active' : 'inactive'}`}><div className="suds-toggle-knob" /></div>
                <button className="text-[8px] font-bold" style={{ color: '#00F0FF', textShadow: '0 0 6px #00F0FF30' }}>Configure</button>
              </div>
            </div>
          ))}
        </div></NP>
        <NP color="#00F0FF"><div className="p-5">
          <h3 className="text-xs font-bold tracking-widest mb-4 suds-cyan-text" style={{ fontFamily: 'Orbitron' }}>PRINTER SETUP</h3>
          {[['Printer-A', 'HP LaserJet', 'Online', 3, '#00F0FF'], ['Printer-B', 'Brother MFC', 'Online', 1, '#D4AF37'], ['Printer-C', 'Epson WorkForce', 'Offline', 0, '#EF4444']].map(([n, m, st, q, c], i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border mb-2" style={{ background: '#08080f', borderColor: `${c}20` }}>
              <div className="flex items-center gap-3"><Printer className="w-4 h-4" style={{ color: c, filter: `drop-shadow(0 0 3px ${c})` }} /><div><div className="text-[10px] text-gray-200">{n} <span className="text-gray-500">({m}, <span style={{ color: st === 'Online' ? '#22C55E' : '#EF4444' }}>{st}</span>)</span></div><div className="text-[8px] text-gray-500">Queue: {q} Jobs</div></div></div>
              <button className="text-[9px] px-3 py-1.5 rounded-lg border text-gray-400 hover:text-white" style={{ borderColor: '#ffffff10', background: '#0a0a14' }}>Manage</button>
            </div>
          ))}
        </div></NP>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN SUDS CONSOLE
// ============================================================================
export default function SUDSConsolePage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSidebar, setActiveSidebar] = useState('dashboard');
  const [devices, setDevices] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [health, setHealth] = useState(null);
  const [qslmError, setQslmError] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    try {
      const [devRes, intRes, taskRes, healthRes] = await Promise.all([
        fetch(`${API}/suds/devices`, { headers }),
        fetch(`${API}/suds/integrations`, { headers }),
        fetch(`${API}/suds/tasks`, { headers }),
        fetch(`${API}/suds/health`, { headers }),
      ]);
      if (devRes.ok) setDevices((await devRes.json()).devices || []);
      if (intRes.ok) setIntegrations((await intRes.json()).integrations || []);
      if (taskRes.ok) setTasks((await taskRes.json()).tasks || []);
      if (healthRes.ok) setHealth(await healthRes.json());
    } catch (err) { console.error('SUDS fetch error:', err); }
  }, [token]);

  useEffect(() => {
    if (token) { fetchAll(); const iv = setInterval(fetchAll, 30000); return () => clearInterval(iv); }
  }, [token, fetchAll]);

  return (
    <div className="min-h-screen flex text-gray-100 bg-texture" data-testid="suds-console">

      {/* === LEFT SIDEBAR === */}
      <aside className="w-16 lg:w-48 flex-shrink-0 border-r border-[--ch-border] flex flex-col items-center lg:items-stretch py-5 px-2 lg:px-4 relative z-10"
        style={{ background: 'var(--ch-bg-primary)' }}>
        <div className="mb-8 text-center relative z-10">
          <div className="text-xl font-black tracking-wider" style={{ fontFamily: 'Orbitron', background: 'linear-gradient(135deg, #D4AF37, #F5D76E, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SXI</div>
          <div className="text-[9px] font-bold tracking-[0.2em] mt-1 neon-cyan" style={{ fontFamily: 'Orbitron' }}>SUDS</div>
        </div>
        <nav className="flex-1 space-y-1.5 relative z-10">
          {SIDEBAR_ITEMS.map(item => {
            const isA = activeSidebar === item.id;
            return (
              <button key={item.id} onClick={() => setActiveSidebar(item.id)} data-testid={`sidebar-${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left ${isA ? 'bg-[rgba(0,240,255,0.08)] border border-[rgba(0,240,255,0.15)]' : 'hover:bg-[rgba(255,255,255,0.02)]'}`}
                style={isA ? { boxShadow: '0 0 12px rgba(0,240,255,0.08)' } : {}}>
                <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: isA ? 'var(--ch-cyan)' : '#444' }} />
                <span className={`text-[10px] hidden lg:block font-bold ${isA ? 'text-[--ch-cyan]' : 'text-[--ch-text-secondary]'}`}>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* === MAIN === */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* === SUDS BANNER HEADER — matches mockup: big gold logo + page title + DNA/hex graphics === */}
        <header className="relative overflow-hidden border-b border-[--ch-border]" style={{ background: 'var(--ch-bg-primary)' }} data-testid="suds-header">
          <div className="absolute top-0 right-0 w-64 h-full bg-[--ch-cyan] rounded-full opacity-[0.02] blur-[80px]" />
          <div className="px-5 py-4 flex items-center gap-4 relative z-10">
            <img src="/suds-logo.png" alt="SUDS" className="h-16 lg:h-20 object-contain" style={{ filter: 'drop-shadow(0 0 12px rgba(0,240,255,0.2))' }} data-testid="suds-logo-img" />
            <div>
              <div className="text-2xl lg:text-3xl font-black text-white tracking-wide" style={{ fontFamily: 'Orbitron' }}>
                — {HEADER_TABS.find(t => t.id === activeTab)?.label?.toUpperCase() || 'OVERVIEW'}
              </div>
            </div>
          </div>
          <div className="px-5 pb-3 flex gap-1 overflow-x-auto">
            {HEADER_TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`px-3 py-1.5 text-[10px] font-bold rounded-t-lg whitespace-nowrap transition-all ${activeTab === tab.id ? 'text-[--ch-cyan] bg-[rgba(0,240,255,0.06)]' : 'text-[--ch-text-secondary] hover:text-white hover:bg-[rgba(255,255,255,0.02)]'}`}
                style={activeTab === tab.id ? { borderBottom: '2px solid var(--ch-cyan)' } : {}}>
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        {qslmError && <div className="px-5 pt-3"><QSLM403Display message={qslmError} onDismiss={() => setQslmError(null)} /></div>}

        <main className="flex-1 overflow-y-auto relative">
          {/* Mockup artwork as ambient texture — same technique as LandingPage/ScanDashboard */}
          <div className="absolute inset-0 pointer-events-none">
            <img src={SCREEN_BG[activeTab] || SCREEN_BG['overview']} alt="" className="w-full h-full object-cover opacity-[0.15]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[--ch-bg-primary] via-[rgba(13,13,13,0.5)] to-[rgba(13,13,13,0.7)]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[rgba(13,13,13,0.3)] to-[rgba(13,13,13,0.6)]" />
          </div>
          <div className="relative z-10 max-w-[1600px] mx-auto px-5 py-5 animate-fade-in-up">
            {activeTab === 'overview' && <OverviewPanel health={health} devices={devices} />}
            {activeTab === 'task-manager' && <TaskManagerPanel tasks={tasks} />}
            {activeTab === 'alert-center' && <AlertCenterPanel />}
            {activeTab === 'retail' && <RetailPanel />}
            {activeTab === 'finance' && <FinancePanel />}
            {activeTab === 'marketing' && <MarketingPanel />}
            {activeTab === 'capability-gates' && <CapabilityGatesPanel />}
            {activeTab === 'user-settings' && <UserSettingsPanel devices={devices} integrations={integrations} />}
            {activeTab === 'topology' && <OverviewPanel health={health} devices={devices} />}
            {activeTab === 'diagnostics' && <AlertCenterPanel />}
          </div>
        </main>

        <footer className="border-t border-[--ch-border] px-5 py-3" style={{ background: 'var(--ch-bg-primary)' }} data-testid="quick-actions-bar">
          <div className="flex items-center gap-3">
            <span className="font-label text-[--ch-text-secondary] mr-3">Quick Actions</span>
            {QUICK_ACTIONS.map(a => (
              <button key={a.id} data-testid={`qa-${a.id}`}
                className="flex-1 py-2.5 rounded-lg text-[10px] font-black flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #D4AF37, #B8962E)', color: '#000', fontFamily: 'Orbitron', boxShadow: '0 0 15px rgba(212,175,55,0.2)' }}>
                <a.icon className="w-3.5 h-3.5" />{a.label}
              </button>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
}
